import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './AdminTools.css';
import ArtistAccounts from './ArtistAccounts';
import ArtistPreview from './ArtistPreview';
import { Calendar } from 'react-multi-date-picker';
import moment from 'moment';

const AdminTools = ({ selectedLocationId }) => {
  const [artists, setArtists] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  
  const [days, setDays] = useState([]);
  const [newArtistName, setNewArtistName] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [showPastDays, setShowPastDays] = useState(false);
  const [showAddArtistModal, setShowAddArtistModal] = useState(false);
  const [showAddDaysModal, setShowAddDaysModal] = useState(false);
  const [daysSaving, setDaysSaving] = useState(false);
  const [daysError, setDaysError] = useState('');
  const [selectedDayIds, setSelectedDayIds] = useState([]);
  const [showReassignPanel, setShowReassignPanel] = useState(false);
  const [targetArtistId, setTargetArtistId] = useState('');
  const [isBackupLoading, setIsBackupLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragSelectMode, setDragSelectMode] = useState(true);
  const [activeArtistId, setActiveArtistId] = useState(null);      // fürs Anzeigen
  const [selectedArtistIds, setSelectedArtistIds] = useState([]);  // fürs Zuweisen/Anlegen
  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState([]);






  // Helper Funktion
  const getLEDColor = (bookingStatus) => {
    switch (bookingStatus) {
      case 'red':
        return 'red';
      case 'yellow':
        return 'yellow';
      default:
        return 'green';
    }
  };

  const groupDaysByMonth = (days) => {
    return days.reduce((groups, day) => {
      const monthKey = moment(day.day).format('MMMM YYYY');
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(day);
      return groups;
    }, {});
  };



  useEffect(() => {
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);


  // Fetch artists and available days when location changes
  useEffect(() => {
    if (selectedLocationId) {
      const fetchData = async () => {
        try {
          const artistsRes = await axios.get(
            `/api/artists?locationId=${selectedLocationId}`
          );
          const daysRes = await axios.get(
            `/api/available_days_with_artists?locationId=${selectedLocationId}`
          );
          const bookingStatusRes = await axios.get(
            `/api/booking-status`
          );

          // Merge bookingStatus into days
          const availableDaysMerged = daysRes.data.map((day) => {
            const matchingStatus = bookingStatusRes.data.find(
              (status) =>
                status.artist_id === day.artistId &&
                moment(status.day).isSame(moment(day.day), 'day')
            );

            return {
              ...day,
              bookingStatus: matchingStatus ? matchingStatus.bookingStatus : 'green', // fallback green
            };
          });

          setArtists(artistsRes.data);
          setAvailableDays(availableDaysMerged);
        } catch (err) {
          console.error('Error fetching artists, days, or booking status:', err.message);
        }
      };


      setArtists([]);
      setAvailableDays([]);
      fetchData();
    }
  }, [selectedLocationId]);

  // Add artist
  const handleAddArtist = async () => {
    if (!newArtistName || !selectedLocationId) return;
    try {
      const response = await axios.post('/api/artists', {
        name: newArtistName,
        locationId: selectedLocationId,
      });
      setArtists([...artists, response.data]);
      setNewArtistName('');
    } catch (err) {
      console.error('Error adding artist:', err.message);
    }
  };


  const handleAddAvailableDays = async () => {
    if (selectedArtistIds.length === 0 || !days.length || !selectedLocationId) {
      setDaysError('Bitte mindestens einen Tag auswählen.');
      return false;
    }

    setDaysSaving(true);
    setDaysError('');
    try {
      const formattedDays = days.map((day) => {
        if (moment.isMoment(day)) return day.format('YYYY-MM-DD');
        if (typeof day === 'string') return day;
        if (day?.format) return day.format('YYYY-MM-DD');
        return moment(day).format('YYYY-MM-DD');
      });

      // duplicates pro artist check
      const duplicatesByArtist = [];

      for (const artistId of selectedArtistIds) {
        const existingDates = availableDays
          .filter(d => d.artistId === Number(artistId))
          .map(d => moment(d.day).format('YYYY-MM-DD'));

        const duplicates = formattedDays.filter(date => existingDates.includes(date));
        if (duplicates.length > 0) {
          const name = artists.find(a => a.id === artistId)?.name || `ID ${artistId}`;
          duplicatesByArtist.push(`${name}: ${duplicates.join(', ')}`);
        }
      }

      if (duplicatesByArtist.length > 0) {
        setDuplicateWarning(`⚠️ Doppelte Tage gefunden:\n${duplicatesByArtist.join('\n')}`);
        return false;
      }

      setDuplicateWarning('');

      await Promise.all(
        selectedArtistIds.map((artistId) =>
          axios.post('/api/available_days', {
            artistId,
            days: formattedDays,
            locationId: selectedLocationId,
          })
        )
      );

      const availableDaysResponse = await axios.get(
        `/api/available_days_with_artists?locationId=${selectedLocationId}`
      );
      setAvailableDays(availableDaysResponse.data);
      setDays([]);
      setShowAddDaysModal(false);
      return true;
    } catch (err) {
      console.error('Error adding available days:', err.response?.data || err.message);
      setDaysError(err.response?.data?.message || 'Tage konnten nicht gespeichert werden.');
      return false;
    } finally {
      setDaysSaving(false);
    }
  };




  // Delete day
  const handleDeleteAvailableDay = async (dayId) => {
    try {
      await axios.delete(`/api/available_days/${dayId}`);
      setAvailableDays(prev => prev.filter(day => day.dayId !== dayId));
    } catch (err) {
      console.error('Error deleting day:', err.message);
    }
  };

  // Toggle appointment selection (MULTI)
  const toggleAppointmentId = (appointmentId) => {
    const id = Number(appointmentId);
    setSelectedAppointmentIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  // Delete selected appointments (MULTI)
  const handleDeleteSelectedAppointments = async () => {
    const uniqueIds = [...new Set(selectedAppointmentIds.map(Number))];

    if (uniqueIds.length === 0) return;

    if (!window.confirm(`❗ ${uniqueIds.length} Termine wirklich löschen?`)) return;

    try {
      await Promise.all(
        uniqueIds.map((id) =>
          axios.delete(`/api/appointments/${id}`)
        )
      );

      // UI sofort aktualisieren (ohne extra Fetch)
      setAvailableDays((prev) =>
        prev.map((d) => ({
          ...d,
          appointments: (d.appointments || []).filter(
            (ap) => !uniqueIds.includes(Number(ap.id))
          ),
        }))
      );

      setSelectedAppointmentIds([]);
    } catch (err) {
      console.error(
        'Error deleting appointments:',
        err.response?.status,
        err.response?.data || err.message
      );
    }
  };


  return (
    <div className="admin-tools-container">
      <div className="settings-intro"><span>PRIME INK · SETTINGS</span><h1>Studioverwaltung</h1><p>Künstler, Zugänge, Arbeitstage und Backups an einem Ort.</p></div>
      <ArtistPreview />
      <ArtistAccounts locationId={selectedLocationId} availableDays={availableDays} onPlanDays={(artistId) => {setSelectedArtistIds([Number(artistId)]);setDays([]);setDuplicateWarning('');setDaysError('');setShowAddDaysModal(true);}} onArtistCreated={async () => {
        try {
          const [artistsRes, daysRes, bookingStatusRes] = await Promise.all([
            axios.get(`/api/artists?locationId=${selectedLocationId}`),
            axios.get(`/api/available_days_with_artists?locationId=${selectedLocationId}`),
            axios.get('/api/booking-status'),
          ]);
          const statuses = Array.isArray(bookingStatusRes.data) ? bookingStatusRes.data : [];
          const refreshedDays = daysRes.data.map(day => {
            const status = statuses.find(item =>
              Number(item.artist_id) === Number(day.artistId) &&
              moment(item.day).isSame(moment(day.day), 'day')
            );
            return { ...day, bookingStatus: status ? status.bookingStatus : 'green' };
          });
          setArtists(artistsRes.data);
          setAvailableDays(refreshedDays);
        } catch (e) {
          console.error('Künstlerkalender konnte nicht aktualisiert werden:', e);
          throw e;
        }
      }} />
      {/* Top row: Location selector and backup */}
      <div
        className="location-backup-row"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '20px' }}
      >

        <button
          className="dark-button"
          onClick={async () => {
            try {
              const res = await axios.post('/api/backup');
              alert(`✅ Datenbank-Backup erfolgreich!\nDatei: ${res.data.file}`);
            } catch (err) {
              alert(`❌ Backup fehlgeschlagen:\n${err.response?.data?.message || err.message}`);
            }
          }}
        >
          📦 Backup Now
        </button>

        <button
          className="dark-button"
          disabled={isBackupLoading}
          onClick={async () => {
            setIsBackupLoading(true);
            try {
              const res = await axios.post('/api/backup-app');
              alert(`✅ System-Backup erfolgreich!\nDatei: ${res.data.file}`);
            } catch (err) {
              alert(`❌ System-Backup fehlgeschlagen:\n${err.response?.data?.message || err.message}`);
            } finally {
              setIsBackupLoading(false);
            }
          }}
        >
          {isBackupLoading ? '⏳ Backup läuft...' : '💾 Backup System'}
        </button>

      </div>


      {/* Modals */}
      {showAddArtistModal && (
        <div className="modal-overlay" onClick={() => setShowAddArtistModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Neuen Künstler hinzufügen</h3>

            <input
              type="text"
              value={newArtistName}
              onChange={(e) => setNewArtistName(e.target.value)}
              placeholder="Künstlername"
            />

            <div className="modal-buttons" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                className="modal-confirm"
                onClick={() => {
                  handleAddArtist();
                  setShowAddArtistModal(false);
                }}
              >
                {daysSaving ? "Speichert …" : "Tage hinzufügen"}
              </button>

              <button
                className="modal-cancel"
                onClick={() => setShowAddArtistModal(false)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}


      {showAddDaysModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              Verfügbare Tage hinzufügen für{' '}
              {selectedArtistIds
                .map(id => artists.find(a => a.id === id)?.name)
                .filter(Boolean)
                .join(', ')
              }
            </h3>

            <Calendar
              multiple
              value={days}
              onChange={(selectedDays) => setDays(selectedDays)}
              format="YYYY-MM-DD"
            />

            {daysError && <p role="alert" style={{color:"#d14b62"}}>{daysError}</p>}
            {duplicateWarning && (
              <div style={{ color: '#ff9800', marginTop: '10px' }}>
                ⚠️ {duplicateWarning}
              </div>
            )}

            <div className="modal-buttons">
              <button
                className="modal-confirm"
                disabled={daysSaving || !days.length}
                onClick={handleAddAvailableDays}
              >
                Hinzufügen
              </button>

              <button className="modal-cancel" onClick={() => setShowAddDaysModal(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Artist */}

      {/* Add Artist */}
      <button
        className="dark-button"
        onClick={() => setShowAddArtistModal(true)}
      >
        + Künstler hinzufügen
      </button>

      <br />

      {/* Add Days */}
      <button
        className="dark-button"
        onClick={() => {
          if (selectedArtistIds.length === 0) {
            return alert('Bitte mindestens einen Künstler auswählen.');
          }
          setShowAddDaysModal(true);
        }}
        style={{ marginBottom: '15px' }}
      >
        + Tage hinzufügen
      </button>





      {/* Grouped Artist-Day Table */}
      <div className="admin-table-layout">
        <div className="artist-list">
          <h3>Künstler</h3>

          {artists.map((artist) => {
            const isSelected = selectedArtistIds.includes(artist.id);
            const isActive = activeArtistId === artist.id;

            return (
              <div
                key={artist.id}
                className={`artist-row ${isActive ? 'active' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {
                    setSelectedArtistIds((prev) =>
                      prev.includes(artist.id)
                        ? prev.filter((id) => id !== artist.id)
                        : [...prev, artist.id]
                    );

                    // Wenn noch kein Active gesetzt ist, nimm den ersten ausgewählten
                    setActiveArtistId((prevActive) => prevActive ?? artist.id);
                  }}
                />

                <div
                  style={{ cursor: 'pointer', flexGrow: 1 }}
                  onClick={() => setActiveArtistId(artist.id)}
                >
                  {artist.name}
                </div>
              </div>
            );
          })}
        </div>


        <div className="artist-days-column">
          {activeArtistId ? (

            <>
              <h4>Verfügbare Tage für {artists.find(a => a.id === activeArtistId)?.name}</h4>


              <button
                onClick={() => setShowPastDays(!showPastDays)}
                style={{
                  marginBottom: '10px',
                  padding: '6px 12px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                {showPastDays ? 'Nur zukünftige Tage anzeigen' : 'Alle Tage anzeigen'}
              </button>

              {Object.entries(groupDaysByMonth(
                availableDays
                  .filter(day => day.artistId === Number(activeArtistId))
                  .filter(day => showPastDays || moment(day.day).isSameOrAfter(moment(), 'day'))
                  .sort((a, b) => new Date(a.day) - new Date(b.day))

              )).map(([month, days]) => (
                <div key={month} style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginTop: '20px', borderBottom: '1px solid #555' }}>{month}</h3>
                  <ul style={{ marginTop: '10px' }}>
                    {days.map(day => (
                      <li
                        key={day.dayId}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setIsDragging(true);
                          const isAlreadySelected = selectedDayIds.includes(day.dayId);
                          setDragSelectMode(!isAlreadySelected);

                          setSelectedDayIds(prev => {
                            if (isAlreadySelected) return prev.filter(id => id !== day.dayId);
                            return prev.includes(day.dayId) ? prev : [...prev, day.dayId];
                          });
                        }}
                        onMouseEnter={() => {
                          if (!isDragging) return;

                          setSelectedDayIds(prev => {
                            if (dragSelectMode) return prev.includes(day.dayId) ? prev : [...prev, day.dayId];
                            return prev.filter(id => id !== day.dayId);
                          });
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          gap: 6,
                          marginBottom: 8,
                          cursor: 'pointer'
                        }}
                      >
                        {/* TOP ROW: Day checkbox + date + LED */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                          <input
                            type="checkbox"
                            checked={selectedDayIds.includes(day.dayId)}
                            readOnly
                            onClick={(e) => e.stopPropagation()}
                          />

                          <div style={{ flexGrow: 1 }}>
                            <strong>{moment(day.day).locale('de').format('dddd')}</strong> – {moment(day.day).format('DD.MM.YYYY')}
                          </div>

                          <span
                            className="day-led"
                            style={{
                              backgroundColor: getLEDColor(day.bookingStatus),
                              width: 15,
                              height: 15,
                              borderRadius: '50%',
                            }}
                            title={`Buchungsstatus: ${day.bookingStatus}`}
                          />
                        </div>

                        {/* APPOINTMENTS LIST */}
                        {day.appointments?.length > 0 && (
                          <div style={{ marginLeft: 28 }}>
                            {day.appointments.map((ap) => (
                              <div
                                key={ap.id}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedAppointmentIds.includes(Number(ap.id))}
                                  onChange={() => toggleAppointmentId(ap.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ flexGrow: 1 }}>
                                  <strong>{String(ap.time).slice(0, 5)}</strong> – {ap.clientName}
                                  {ap.phone ? ` (${ap.phone})` : ''}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </li>


                    ))}
                  </ul>
                </div>
              ))}



              {selectedDayIds.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    className="modal-cancel"
                    onClick={async () => {
                      for (const id of selectedDayIds) {
                        await handleDeleteAvailableDay(id);
                      }
                      setSelectedDayIds([]);
                    }}
                  >
                    Ausgewählte löschen
                  </button>

                  <button
                    className="modal-confirm"
                    style={{ marginLeft: '10px' }}
                    onClick={() => setShowReassignPanel(true)}
                  >
                    Künstler zuweisen
                  </button>
                </div>
              )}

              {selectedAppointmentIds.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    className="modal-cancel"
                    onClick={handleDeleteSelectedAppointments}
                  >
                    🗑️ {selectedAppointmentIds.length} Termine löschen
                  </button>
                </div>
              )}

              {showReassignPanel && (
                <div style={{ marginTop: '15px' }}>
                  <select
                    value={targetArtistId}
                    onChange={(e) => setTargetArtistId(e.target.value)}
                  >
                    <option value="">Künstler auswählen</option>
                    {artists
                      .filter(a => a.id !== Number(activeArtistId))
                      .map(artist => (
                        <option key={artist.id} value={artist.id}>
                          {artist.name}
                        </option>
                      ))}
                  </select>

                  <button
                    className="modal-confirm"
                    disabled={!targetArtistId}
                    onClick={async () => {
                      try {
                        await axios.put('/api/available_days/reassign', {
                          dayIds: selectedDayIds,
                          newArtistId: targetArtistId,
                          locationId: selectedLocationId,
                        });


                        const res = await axios.get(
                          `/api/available_days_with_artists?locationId=${selectedLocationId}`
                        );
                        setAvailableDays(res.data);
                        setSelectedDayIds([]);
                        setTargetArtistId('');
                        setShowReassignPanel(false);
                      } catch (err) {
                        console.error('Fehler beim Umsortieren:', err.message);
                      }
                    }}
                  >
                    Zuweisen
                  </button>
                </div>
              )}
            </>
          ) : (
            <p>Wähle einen Künstler, um Tage anzuzeigen.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTools;
